import {Box} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {useRouter} from 'next/router';
import {useEffect, useRef} from 'react';
import type {GetCompanyInfoResponse} from '../../../core/repositories/companiesRepository';
import type {GetCompanyDetailResponse} from '../../../core/repositories/companyDetailRepository';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {convertCompanyIndustryCategory} from '../usecase/convertCompanyIndustryCategory';
import {useCompanyDetailContents} from '../usecase/useCompanyDetailContents';
import {useHandleCompanyDetail} from '../usecase/useHandleCompanyDetail';
import {CompanyDetailBody} from './Container/CompanyDetailBody';
import {CompanyFollowCompletionModalContainer} from './Container/CompanyFollowCompletionModalContainer';
import {CompanyHeaderWrapperPc} from './Container/Presenter/CompanyHeaderWrapperPc';
import {CompanyHeaderWrapperSp} from './Container/Presenter/CompanyHeaderWrapperSp';
import {ContentNavigationBar} from './Container/Presenter/ContentNavigationBar';
import {MainImagePc} from './Container/Presenter/MainImagePc';
import {PcFloatEntryButton} from './Container/Presenter/PcFloatEntryButton';
import {SpFloatContent} from './Container/Presenter/SpFloatContent';
import {EntryButtonSP} from './EntryButton/sp';
import {RecruitmentNotification} from './RecruitmentNotification';
import {TableOfContentsModal} from './Table0fContents';

type Props = {
  companyDetail: GetCompanyDetailResponse;
  companyInfo: GetCompanyInfoResponse;
  year?: string;
  isPreview?: boolean;
  isLogin: boolean;
};

export const CompanyDetailPage = ({
  companyDetail,
  companyInfo,
  year = '',
  isPreview = false,
  isLogin,
}: Props) => {
  const {isPC} = useMediaQuery();
  const router = useRouter();
  const {
    nativeAds,
    companyPageColumnPosts,
    recruitments,
    sameIndustryCompanies,
    groupCompanies,
  } = useHandleCompanyDetail({
    companyDetail,
    companyInfo,
    year,
  });

  const [
    isOpenTableOfContentsModal,
    {open: openTableOfContentsModal, close: closeTableOfContentsModal},
  ] = useDisclosure(false);
  const [recruitmentNotification, {close: closeRecruitmentNotification}] =
    useDisclosure(true);
  const [openedPcFloatEntryButton, {close: closePcFloatEntryButton}] =
    useDisclosure(true);

  const {
    isContents,
    entryURLForTracking,
    recruitingFairRegistrationURLForTracking,
    followCountUpdated,
    isFollowed,
    handleFollowCompany,
    headerContents,
    interestsId,
    filteredRecruitments,
    followCompletionModalOpened,
    closeFollowCompletionModal,
    followCompletionSuppressChecked,
    setFollowCompletionSuppressChecked,
  } = useCompanyDetailContents({
    companyDetail,
    companyInfo,
    nativeAds,
    companyPageColumnPosts,
    recruitments,
    sameIndustryCompanies,
    groupCompanies,
    isPreview,
    isValid: isLogin,
    isPC,
    sourceURL: router.asPath,
  });

  const prevHeaderContentsRef = useRef(headerContents);

  const {industryCategoryId, industryCategoryName, industryIds} =
    convertCompanyIndustryCategory({
      industryCategoryId: companyDetail.metaData?.industryCategoryID,
      industryCategoryName: companyDetail.metaData?.industryCategoryName,
      industryId: companyInfo.industryID,
    });

  useEffect(() => {
    // PCの時のみ、スクロール位置を変更
    if (isPC) {
      document.documentElement.classList.remove('scroll-pt-[150px]');
      document.documentElement.classList.add('scroll-pt-[200px]');
    }
  }, [isPC]);

  useEffect(() => {
    if (
      JSON.stringify(prevHeaderContentsRef.current) !==
      JSON.stringify(headerContents)
    ) {
      // コンテンツが読み込まれてから、ハッシュリンクをスクロール
      if (window.location.hash) {
        const target = document.querySelector(window.location.hash);
        if (target) {
          window.location.hash = '';
          window.location.hash = target.id;
        }
      } else {
        window.scrollTo(0, 0);
      }
    }

    prevHeaderContentsRef.current = headerContents;
  }, [isContents]);

  return (
    <>
      {isPreview && (
        <div className="fixed top-0 left-0 w-full h-12 bg-black-1 text-white text-center flex items-center justify-center p-32 z-50">
          プレビューモード
        </div>
      )}
      <Box className="block">
        <div
          className={`pc:w-[1200px] mx-auto ${isLogin ? '' : 'pc:flex pc:justify-center'}`}
        >
          <div className="bg-background-1">
            <SpFloatContent openModal={openTableOfContentsModal} />
            <div className="hidden pc:block">
              <CompanyHeaderWrapperPc
                companyInfo={companyInfo}
                companyDetail={companyDetail}
                companyName={
                  companyDetail.companyData?.companyName ??
                  companyDetail.metaData?.companyName
                }
                industryName={
                  companyDetail.companyData?.industry ??
                  companyDetail.metaData?.industryName
                }
                companyLogoURL={companyDetail.metaData?.logoURL}
                crumbIndustryName={industryCategoryName}
                industryCategoryId={industryCategoryId}
                industryIds={industryIds}
                isFollowed={isFollowed}
                followCount={followCountUpdated}
                onFollow={handleFollowCompany}
                entryURL={entryURLForTracking}
                recruitingFairRegistrationURL={
                  recruitingFairRegistrationURLForTracking
                }
                interestsId={interestsId}
                isEngineerCompany={companyInfo.isEngineerCompany}
              />
            </div>
            <div className="pc:hidden">
              <CompanyHeaderWrapperSp
                companyInfo={companyInfo}
                companyDetail={companyDetail}
                companyName={
                  companyDetail.companyData?.companyName ??
                  companyDetail.metaData?.companyName
                }
                industryName={
                  companyDetail.companyData?.industry ??
                  companyDetail.metaData?.industryName
                }
                companyLogoURL={companyDetail.metaData?.logoURL}
                mainPhotoURL={companyDetail.metaData?.mainPhotoURL}
                vimeoId={companyDetail.metaData?.vimeoID}
                crumbIndustryName={industryCategoryName}
                industryCategoryId={industryCategoryId}
                industryIds={industryIds}
                industryCategoryName={industryCategoryName}
                isFollowed={isFollowed}
                followCount={followCountUpdated}
                onFollow={handleFollowCompany}
                entryURL={entryURLForTracking}
                recruitingFairRegistrationURL={
                  recruitingFairRegistrationURLForTracking
                }
                isValid={isLogin}
                availableGraduationYears={
                  companyDetail.metaData?.availableGraduationYears
                }
                year={year}
                isEngineerCompany={companyInfo.isEngineerCompany}
                openTableOfContentsModal={openTableOfContentsModal}
              />
            </div>
            <ContentNavigationBar
              companyId={companyDetail.metaData.companyID}
              hasReports={companyDetail.metaData.hasReports ?? false}
              headerContents={headerContents}
              isValid={isLogin}
              availableGraduationYears={
                companyDetail.metaData?.availableGraduationYears
              }
              year={year}
              className="hidden pc:block"
            />
            <MainImagePc
              mainPhotoURL={companyDetail.metaData?.mainPhotoURL}
              vimeoId={companyDetail.metaData?.vimeoID}
              companyName={companyDetail.metaData?.companyName}
            />
            <CompanyDetailBody
              companyDetail={companyDetail}
              companyInfo={companyInfo}
              sections={headerContents}
              nativeAds={nativeAds}
              recruitments={recruitments}
              filteredRecruitments={filteredRecruitments}
              companyPageColumnPosts={companyPageColumnPosts}
              sameIndustryCompanies={sameIndustryCompanies}
              groupCompanies={groupCompanies}
              isContents={isContents}
              entryURLForTracking={entryURLForTracking || ''}
              recruitingFairRegistrationURLForTracking={
                recruitingFairRegistrationURLForTracking || ''
              }
              isPC={isPC}
              isLogin={isLogin}
            />
          </div>
        </div>
      </Box>
      {filteredRecruitments &&
        filteredRecruitments.length > 0 &&
        companyDetail.metaData.logoURL && (
          <RecruitmentNotification
            recruitmentList={filteredRecruitments}
            companyLogoURL={companyDetail.metaData?.logoURL}
            companyName={companyDetail.metaData?.companyName}
            hasEntryButton={Boolean(
              entryURLForTracking || recruitingFairRegistrationURLForTracking,
            )}
            mounted={recruitmentNotification}
            close={closeRecruitmentNotification}
          />
        )}
      <PcFloatEntryButton
        companyName={companyDetail.metaData?.companyName}
        logoURL={companyDetail.metaData?.logoURL}
        entryURL={entryURLForTracking}
        registrationURL={recruitingFairRegistrationURLForTracking}
        companyId={companyDetail.metaData.companyID}
        followerCount={followCountUpdated}
        isFollowed={isFollowed}
        onFollow={handleFollowCompany}
        opened={openedPcFloatEntryButton}
        close={closePcFloatEntryButton}
      />
      <EntryButtonSP
        entryURL={entryURLForTracking}
        registrationURL={recruitingFairRegistrationURLForTracking}
        companyId={companyDetail.metaData.companyID}
        companyName={companyDetail.metaData?.companyName}
        isFollowed={isFollowed}
        followCount={followCountUpdated}
        onFollow={handleFollowCompany}
      />
      <TableOfContentsModal
        headerContents={headerContents}
        hasReports={companyDetail.metaData?.hasReports ?? false}
        companyID={companyDetail.metaData?.companyID}
        isOpen={isOpenTableOfContentsModal}
        onClose={closeTableOfContentsModal}
      />
      <CompanyFollowCompletionModalContainer
        companyId={companyDetail.metaData.companyID}
        companyName={
          companyDetail.companyData?.companyName ??
          companyDetail.metaData?.companyName ??
          ''
        }
        opened={followCompletionModalOpened}
        onClose={closeFollowCompletionModal}
        checked={followCompletionSuppressChecked}
        setChecked={setFollowCompletionSuppressChecked}
        recruitmentLinkHref="#recruitment"
      />
    </>
  );
};
